import {
  api,
  authApi,
  setAccessToken,
  setRefreshToken,
  removeAllTokens,
} from "./api";

/*
 * ТОЛЬКО ЛОГИН И ПАРОЛЬ. Бэкенд Атырау валидирует тело строго и на любое лишнее
 * поле (снимок лица, данные устройства) отвечает 400 — при верных учётных
 * данных. Добавить сюда поле значит сломать вход.
 */
export interface LoginRequest {
  username: string;
  password: string;
}

// Ответ от API авторизации (старый формат)
export interface AuthResponse {
  user_id: number;
  user_name: string;
  first_name: string;
  last_name: string;
  middle_name: string;
  accessToken: string;
  refreshToken: string;
}

/*
 * ПРОФИЛЬ — ИЗ `GET /auth/me`, ЕДИНСТВЕННОЙ РУЧКИ ПРОФИЛЯ У БЭКЕНДА АТЫРАУ.
 *
 * Здесь были `/users/me`, `/users/me/photo`, `/positions` и `/positions/:id`
 * республиканского продукта. У `sc_atyrau_backend` ни одной из них нет, и при
 * каждом входе уходило три-четыре запроса в 404, а в шапке вместо имени и
 * должности стоял логин. `/auth/me` отдаёт имя, должности, подразделение и
 * права одним ответом. Фотографии у бэкенда нет — аватар остаётся заглушкой.
 */
export interface CurrentEmployment {
  employmentId: number;
  staffUnitId: number;
  staffTitleRu: string;
  staffTitleKk?: string;
  orgUnitId: number;
  orgUnitNameRu: string;
  orgUnitPath: string;
}

export interface CurrentUser {
  id: number;
  username: string;
  isSuperadmin: boolean;
  mustChangePassword: boolean;
  personId?: number;
  fullName?: string;
  employments: CurrentEmployment[];
  permissions: string[];
}

export function getCurrentUser(): Promise<CurrentUser> {
  // skipCache: профиль читают сразу после входа и смены пользователя — кэш
  // запросов отдал бы прошлого человека.
  return api.get<CurrentUser>("/auth/me", { skipCache: true });
}

/**
 * Выход из системы
 */
export async function logout(): Promise<void> {
  try {
    await authApi.post("/auth/logout");
  } catch (error) {
    console.error("Logout failed:", error);
  } finally {
    removeAllTokens();
  }
}

/**
 * Авторизация пользователя (код + пароль + фото)
 */
export async function login(request: LoginRequest): Promise<AuthResponse> {
  try {
    // Тело собирается по полям, а не передаётся целиком: так лишнее поле не
    // попадёт в запрос, даже если его добавят в тип выше.
    const { data } = await authApi.post<AuthResponse>("/auth/login", {
      username: request.username,
      password: request.password,
    });

    // Сохраняем токены
    if (data.accessToken) {
      setAccessToken(data.accessToken);
    }
    if (data.refreshToken) {
      setRefreshToken(data.refreshToken);
    }

    return data;
  } catch (error) {
    console.error("Login failed:", error);
    throw error;
  }
}

/** Ответ `/auth/login` бэкенда Атырау (`sc_atyrau_backend`). */
export interface ScTokenPair {
  accessToken: string
  refreshToken: string
  tokenType: string
  expiresIn: number
  refreshExpiresAt: string
  mustChangePassword: boolean
}

/**
 * Вход в `sc_atyrau_backend` — отдельно от `login()` выше, и это не дубль.
 *
 * ТЕЛО ТОЛЬКО ИЗ ДВУХ ПОЛЕЙ — как и у `login()` выше, который с 22.09.2026
 * тоже шлёт ровно `username` и `password`. Обе функции оставлены: `login()`
 * дополнительно тянет профиль и фото пользователя, здесь же нужен только
 * обмен пары на токен.
 *
 * Токены кладём сразу: `getAccessToken()` читает их из localStorage и с этого
 * момента подписывает ими все запросы.
 */
export async function loginToScBackend(
  username: string,
  password: string,
): Promise<ScTokenPair> {
  const { data } = await authApi.post<ScTokenPair>('/auth/login', { username, password })
  if (data.accessToken) setAccessToken(data.accessToken)
  if (data.refreshToken) setRefreshToken(data.refreshToken)
  return data
}

/**
 * Попытка обновить токен
 */
/*
 * REFRESH ЖИВЁТ В ОДНОМ МЕСТЕ — `api.ts`. Здесь была вторая реализация со своей
 * блокировкой; при загрузке страницы две реализации предъявляли один и тот же
 * одноразовый refresh-токен, и бэкенд закрывал все сессии аккаунта. Реэкспорт
 * сохраняет прежний импорт для `AuthContext`.
 */
export { tryRefreshToken } from "./api";
